import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Check, Copy, ExternalLink, Send } from 'lucide-react';
import QRCodeComponent from '../../../../components/QRCodeComponent';
import { copyToClipboard } from '../../../../utils/native/copy';
import { QUIZ_LINK_PREFIX, quizLinkOf, removeQuiz, sendQuizInvitation } from '../../../../store/clientQuiz/cache';
import type { AdviserQuiz, QuizPart, QuizType } from '../../../../store/clientQuiz/api';
import { pickCopy } from '../../../../utils/localeCopy';
import { QUIZ_LANGS, quizLangOf } from '../../../CoverQuiz/quizLang';
import type { QuizLang } from '../../../CoverQuiz/quizLang';

/** Each language named in itself, so a client sitting beside the adviser can read it too. */
const LANG_NAMES: Record<QuizLang, string> = { 'zh-Hant-HK': '繁體中文', zh: '简体中文', en: 'English' };

// The section's own words; the per-quiz ones come in through `copy`.
const UI_COPY = {
  'zh-Hant-HK': {
    resend: '重新發送',
    redo: '再做一次',
    create: '產生連結',
    stale: '未能更新最新狀態，會自動再試。',
    noFactsheet: '這位客戶未有資料表，暫時不能發送測驗。',
    nameLabel: '客戶稱呼',
    sendFailed: '發送失敗，請再試一次。',
    whatsApp: 'WhatsApp 邀請',
    revoke: '收回連結',
    lang: '測驗語言（客戶在頁面上也可以切換）'
  },
  zh: {
    resend: '重新发送',
    redo: '再做一次',
    create: '生成链接',
    stale: '未能更新最新状态，会自动再试。',
    noFactsheet: '这位客户还没有资料表，暂时不能发送测验。',
    nameLabel: '客户称呼',
    sendFailed: '发送失败，请再试一次。',
    whatsApp: 'WhatsApp 邀请',
    revoke: '收回链接',
    lang: '测验语言（客户在页面上也可以切换）'
  },
  en: {
    resend: 'Send again',
    redo: 'Do it again',
    create: 'Create link',
    stale: "Couldn't refresh the latest status. Retrying automatically.",
    noFactsheet: "This client has no factsheet yet, so the quiz can't be sent.",
    nameLabel: 'Client name',
    sendFailed: 'Sending failed. Please try again.',
    whatsApp: 'WhatsApp invite',
    revoke: 'Withdraw link',
    lang: 'Quiz language (the client can also switch on the page)'
  }
};

/**
 * The share section both quiz dialogs open with (保障小測驗 and 職涯測驗), read
 * from the server's own invitation state (spec §5.2, §5.5):
 * - a live (unexpired) pending invitation for this part → its QR code, copy /
 *   WhatsApp / preview (the untokened page, which saves nothing), and a quiet 收回連結;
 * - otherwise a name field (the factsheet's name, editable), the language the
 *   quiz opens in (the adviser's own by default) and one button that
 *   sends a new invitation, worded by why: 產生連結, 重新發送 (expired) or
 *   再做一次 (already completed);
 * - no factsheet → a notice and no button.
 *
 * Test ids are `<testIdPrefix>-send` / `-revoke` / `-no-factsheet`, plus the
 * shared `quiz-display-name` on the name input.
 */

export type QuizShareCopy = {
  heading: string;
  desc: string;
  copyLabel: string;
  copiedLabel: string;
  previewLabel: string;
};

export interface QuizShareSectionProps {
  type: QuizType;
  part: QuizPart;
  factsheetId: string | null;
  /** The pending invitation for this part, if any (expired or not). */
  pending?: AdviserQuiz;
  /** This part is already completed — a new send is a redo. */
  done: boolean;
  /** The name a new invitation is sent with, unless the adviser types another. */
  defaultName?: string;
  /** The WhatsApp message for a link, in the language the link opens in. */
  inviteText: (url: string, lang: QuizLang) => string;
  copy: QuizShareCopy;
  testIdPrefix: string;
  /** The last refresh of the invitation list failed: what is shown may be out of date. */
  stale?: boolean;
  /** react-intl's locale; Traditional Chinese when absent. */
  locale?: string;
}

const QuizShareSection = (props: QuizShareSectionProps) => {
  const { type, part, factsheetId, pending, done, defaultName, inviteText, copy, testIdPrefix, stale, locale } = props;
  const ui = pickCopy(UI_COPY, locale);
  const liveLink = Boolean(pending && !pending.expired && pending.token);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(defaultName || '');
  const [lang, setLang] = useState<QuizLang>(() => quizLangOf(locale));
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  // Guards a double tap landing in the same tick, before `sending` (which disables
  // the button) has even re-rendered — the quiz pages use the same pattern.
  const sendingRef = useRef(false);

  // The dialogs stay mounted while the drawer switches clients: a new client
  // starts from their own name, not the one typed for the last.
  useEffect(() => {
    setName(defaultName || '');
    setSendFailed(false);
  }, [defaultName, factsheetId]);

  useEffect(() => setLang(quizLangOf(locale)), [locale]);

  // A link made before the language was chosen (or a bad value) reads in the adviser's language.
  const linkLang = quizLangOf(pending?.locale || locale);

  // The web app's public address, not `window.location.origin`: inside the
  // iOS/Android app that is the app's own local origin, so a link shared from a
  // phone opened nothing for the client. Local dev has no PFPLUS_URL.
  const origin = process.env.PFPLUS_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const quizUrl = liveLink && pending?.token ? quizLinkOf(origin, type, pending.token) : '';
  // The preview is the page without a token: it saves nothing (spec §5.2). The
  // tokened link would submit the adviser's own test answers as the client's
  // and use up the invitation.
  // 職涯測驗 第二部分's preview is its own page (`?part=2`); `lang` opens it in the link's language.
  const previewQuery = [type === 'career' && part === 2 ? 'part=2' : '', `lang=${linkLang}`].filter(Boolean).join('&');
  const previewUrl = `${origin}/web/${QUIZ_LINK_PREFIX[type]}?${previewQuery}`;

  // Through the app's own helper (utils/native/copy): the Capacitor Clipboard
  // plugin on iOS / Android, where the WebView's async Clipboard API cannot be
  // relied on. 已複製 only when the copy landed (as AdviserInfo does); the link
  // is printed below the buttons either way, to copy by hand.
  const copyLink = async () => {
    let landed = false;
    try {
      landed = await copyToClipboard(quizUrl);
    } catch (_) {
      // leave landed false
    }
    if (!landed) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openWhatsApp = () => {
    // api.whatsapp.com rather than wa.me: wa.me mangles emoji in ?text=.
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(inviteText(quizUrl, linkLang))}`, '_blank');
  };

  const revokeLink = () => {
    if (factsheetId && pending) void removeQuiz(factsheetId, type, pending.id, 'sent');
  };

  const sendLabel = pending && pending.expired ? ui.resend : done ? ui.redo : ui.create;

  const handleSend = () => {
    if (sendingRef.current || !factsheetId) return;
    sendingRef.current = true;
    setSending(true);
    setSendFailed(false);
    sendQuizInvitation(factsheetId, type, part, name.trim() || defaultName || '', lang)
      .catch(() => setSendFailed(true))
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  };

  const staleNotice = stale ? (
    <span className="rqd-share-desc" role="status" data-testid={`${testIdPrefix}-stale`}>
      {ui.stale}
    </span>
  ) : null;

  if (!factsheetId) {
    return (
      <div className="rqd-share-section rqd-anim-item">
        <p className="rqd-share-desc" data-testid={`${testIdPrefix}-no-factsheet`}>
          {ui.noFactsheet}
        </p>
      </div>
    );
  }

  if (!liveLink) {
    return (
      <div className="rqd-share-section rqd-anim-item">
        <div className="rqd-share-copy">
          <TextField
            label={ui.nameLabel}
            value={name}
            onChange={e => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'quiz-display-name' } }}
            size="small"
            fullWidth
          />
          <div className="rqd-share-lang">
            <span className="rqd-share-desc">{ui.lang}</span>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={lang}
              onChange={(_event, value: QuizLang | null) => value && setLang(value)}
              data-testid={`${testIdPrefix}-lang`}
            >
              {QUIZ_LANGS.map(option => (
                <ToggleButton
                  key={option}
                  value={option}
                  data-lang={option}
                  style={{ textTransform: 'none', padding: '2px 10px', fontSize: 12 }}
                >
                  {LANG_NAMES[option]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
          <div className="rqd-share-actions">
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={sending}
              onClick={handleSend}
              data-testid={`${testIdPrefix}-send`}
              style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700 }}
            >
              {sendLabel}
            </Button>
          </div>
          {sendFailed && (
            <span className="rqd-share-desc" role="alert" data-testid={`${testIdPrefix}-send-failed`}>
              {ui.sendFailed}
            </span>
          )}
          {staleNotice}
        </div>
      </div>
    );
  }

  return (
    <div className="rqd-share-section rqd-anim-item">
      <div className="rqd-qr-box">
        {/* Keyed by the link: QRCodeComponent draws only on mount, so a new token
            arriving by the 5 s poll (a re-send from another device) must remount
            it — otherwise the QR keeps the withdrawn link. */}
        <QRCodeComponent
          key={quizUrl}
          content={quizUrl}
          logo={false}
          isStyle="client-export"
          style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          imgStyle={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      </div>
      <div className="rqd-share-copy">
        <div>
          <strong className="rqd-share-heading">{copy.heading}</strong>
          <span className="rqd-share-desc">{copy.desc}</span>
        </div>
        <div className="rqd-share-actions">
          <Button
            variant="outlined"
            size="small"
            onClick={copyLink}
            startIcon={copied ? <Check size={14} color="#16a34a" /> : <Copy size={14} />}
            style={{ fontSize: 12, borderRadius: 8, textTransform: 'none' }}
          >
            {copied ? copy.copiedLabel : copy.copyLabel}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={openWhatsApp}
            startIcon={<Send size={14} color="#25D366" />}
            style={{ fontSize: 12, borderRadius: 8, borderColor: '#25D366', color: '#075e54', textTransform: 'none' }}
          >
            {ui.whatsApp}
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => window.open(previewUrl, '_blank')}
            endIcon={<ExternalLink size={12} />}
            style={{ fontSize: 12, textTransform: 'none' }}
          >
            {copy.previewLabel}
          </Button>
        </div>
        <span className="rqd-share-url" data-testid={`${testIdPrefix}-url`}>
          {quizUrl}
        </span>
        <Button
          variant="text"
          size="small"
          onClick={revokeLink}
          data-testid={`${testIdPrefix}-revoke`}
          style={{ alignSelf: 'flex-start', color: '#94a3b8', fontSize: 11, textTransform: 'none', padding: 0 }}
        >
          {ui.revoke}
        </Button>
        {staleNotice}
      </div>
    </div>
  );
};

export default QuizShareSection;
